/**
 * @fileoverview Service module for handling Trading Mall related API operations
 * @module TradingMallService
 */
import { getSessionData } from "@repo/shared-utils/common-utils";
import { fetchData } from "@repo/shared-utils/fetch-utils";
import { LogUtils } from "@repo/shared-utils/log-utils";
import { IResponse, encryptedRequest } from "@repo/sso/api";
import { API_PATHS } from "@repo/sso/api.constants";
import type { IndexListItem } from "../components/MarketIndicesDashboard/Interface";
import { mapCardDataToNewsItem } from "../components/News/components/newsCardUtils";
import type { INewsCardData } from "../components/News/components/types";
import { HoldingsResponse } from "../components/Portfolio/PortfolioTypes";
import { TradingMallApiEndpoints } from "../constants/TradingMallApiEndpoints";
import { ICorporateActions } from "../interfaces/CorporateAction.interface";
import {
  IInvestmentReturnsData,
  IInvestmentReturnsRequest,
  ITradingMallContent,
} from "../interfaces/ITradingMallContentV1.interface";
import {
  IIpoListAPIPayload,
  IIpoListAPIResponse,
  IIpoListRequest,
  IIpoListResponse,
} from "../interfaces/IpoList.interface";
import type { FinancialNewsItem } from "../interfaces/LatestFinancialResult.interface";
import { MarketTodayApiResponse } from "../interfaces/MarketToday.interface";
import {
  INewsApiResponse,
  INewsItem,
  IOtherNewsData,
  IPersonalizedNewsRequest,
  IPersonalizedNewsResponse,
} from "../interfaces/News.interface";
import {
  INewsFilterApiResponse,
  INewsFilterData,
} from "../interfaces/NewsFilter.interface";
import { ISimilarStocksData } from "../interfaces/SimilarStocks.interface";
import {
  IFiiAndDiiData,
  IGlobalIndicesResponse,
  ISharePriceUpdateData,
  ISharePriceUpdateParams,
  IStockApiRequest,
  IStockApiResponse,
} from "../interfaces/StockApi.interface";
import {
  IStockChartData,
  IStockChartDataRequest,
} from "../interfaces/StockChartData.interface";
import { IStockDetailsData } from "../interfaces/StockDetails.interface";
import { IStockFinancialsData } from "../interfaces/StockFinancials.interface";
import { IStockFundamentalsData } from "../interfaces/StockFundamentals.interface";
import { IStockInfoData } from "../interfaces/StockInfo.interface";
import {
  IStockListData,
  IStockItem,
  IStockListRequest,
} from "../interfaces/StockList.interface";
import { IStockOverviewData } from "../interfaces/StockOverview.interface";
import { IStockTechnicalSummaryData } from "../interfaces/StockTechnicalSummary.interface";
import {
  getMarketInsightsVariant,
  type MarketInsightsVariant,
} from "../components/MarketInsigtsHeading/marketInsightsVariant";

export interface FlipitNewsResponse {
  data: {
    news: FinancialNewsItem[];
    showMore: boolean;
    showLess: boolean;
  };
  statusCode?: number;
  status: string;
  message: string;
}

const SERVER_CALL_PARAM = "tokenSrc=3in1";
const withServerTokenSrc = (endpoint: string): string => {
  if ((globalThis as any).window !== undefined) {
    return endpoint;
  }
  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}${SERVER_CALL_PARAM}`;
};
const getPublicNewsPostOptions = () => {
  // Client-side public news APIs should include CSRF.
  if ((globalThis as any).window !== undefined) {
    return { method: "POST" as const };
  }
  // Server-side calls keep skipCsrf for compatibility.
  return { method: "POST" as const, skipCsrf: true };
};

export const getTradingMallContentV1 = async (
  appVersion: string,
  endpointKey: keyof typeof TradingMallApiEndpoints = "TRADING_MALL_CONTENT",
): Promise<ITradingMallContent> => {
  const endpoint = TradingMallApiEndpoints[endpointKey].replace(
    "{version}",
    appVersion,
  );
  const response: ITradingMallContent = await fetchData({
    endpoint,
    tags: ["TradingMallContent", `TradingMallContent${appVersion}`],
    cache: "force-cache",
  });
  LogUtils.debug("getTradingMallContentV1: ", response);
  return response;
};

export const getMarketHeadingVariant = async (): Promise<MarketInsightsVariant> => {
  if ((globalThis as any).window === undefined) {
    return getMarketInsightsVariant();
  }

  const response = await fetch(TradingMallApiEndpoints.MARKET_HEADING_VARIANT, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    next: { revalidate: 45 },
  });
  const data = await response.json();
  return data?.variant ?? "preMarket";
};
/**
 * Fetches overview data for a specific stock
 * @async
 * @description Gets detailed overview information about a stock including company profile,
 * market status, and basic financial metrics. Data is cached for 45 seconds.
 * @param {Object} params - The stock identification parameters
 * @param {string} params.exchId - Exchange identifier (e.g., "NSE", "BSE")
 * @param {string} params.formattedSymbol - Stock symbol in exchange-specific format
 * @returns {Promise<IStockOverviewData>} Detailed overview data for the stock
 * @throws {Error} When the API request fails or returns invalid data
 * @example
 * const overview = await getStockOverviewData({ exchId: "NSE", formattedSymbol: "TATASTEEL" });
 */
export const getStockOverviewData = async ({
  exchId,
  formattedSymbol,
}: IStockApiRequest): Promise<IStockOverviewData> => {
  const response: IStockApiResponse<IStockOverviewData> = await fetchData({
    endpoint: TradingMallApiEndpoints.ABOUT_STOCK,
    params: { exchId, formattedSymbol },
    revalidate: 45,
  });
  LogUtils.debug("getAboutStock: ", response?.data);
  return response?.data;
};

/**
 * Retrieves detailed market data for a specific stock
 * @async
 * @description Fetches current market data including price, volume, and other trading metrics.
 * This data is not cached and represents real-time information.
 * @param {Object} params - The stock identification parameters
 * @param {string} params.exchId - Exchange identifier (e.g., "NSE", "BSE")
 * @param {string} params.formattedSymbol - Stock symbol in exchange-specific format
 * @returns {Promise<IStockDetailsData>} Current market data and trading details
 * @throws {Error} When the API request fails or returns invalid data
 * @example
 * const details = await getStockDetailsData({ exchId: "NSE", formattedSymbol: "TATASTEEL" });
 */
export const getStockDetailsData = async ({
  exchId,
  formattedSymbol,
}: IStockApiRequest): Promise<IStockDetailsData> => {
  const response: IStockApiResponse<IStockDetailsData> = await fetchData({
    endpoint: TradingMallApiEndpoints.STOCK_DETAILS,
    params: { exchId, formattedSymbol },
    revalidate: 45,
  });
  LogUtils.debug("getStockDetails: ", response?.data);
  return response?.data;
};
/**
 * Fetches updated share price data for a given exchange and symbol.
 *
 * @param {ISharePriceUpdateParams} params - Contains exchId and symbol.
 * @returns {Promise<ISharePriceUpdateData|null>} Share price update data or null if unavailable.
 */


/**
 * Retrieves a filtered list of stocks
 * @async
 * @description Currently returns mock data. When implemented, will fetch a filtered list of stocks
 * based on provided criteria such as sector, market cap, or other filters.
 * @param {IStockListRequest} _body - Filter criteria for the stock list
 * @returns {Promise<IStockListData>} List of stocks matching the filter criteria
 * @throws {Error} When the API request fails (when implemented)
 * Implement actual API integration replacing mock data
 * @example
 * const stocks = await getStockListData({ sector: "IT", marketCap: "LARGE" });
 */
export const getStockListData = async (
  _body: IStockListRequest,
): Promise<IStockListData> => {
  const isClient = (globalThis as unknown as { window?: unknown }).window !== undefined;
  const response = await fetchData({
    options: {
      method: "POST",
      ...(isClient ? {} : { skipCsrf: true }),
    },
    endpoint: withServerTokenSrc(TradingMallApiEndpoints.GET_STOCK_LIST),
    body: _body,
    cache: "no-store",
  });
  if (response?.data) {
    LogUtils.debug("getStockList: ", response.data);
    return response;
  }
  return response.data;
};

export interface ITrendingStocksListRequest extends IStockListRequest {
  tab?: string;
  tabType?: string;
  callType?: string;
  horizon?: string;
  type?: string;
}

export interface IResearchCallItem {
  symbol?: string;
  exchange?: number;
  token?: number;
  xExchToken?: number;
  instType?: string;
  compname?: string;
  calldt?: string;
  ltp?: number;
  recprice?: number;
  targetprice1?: number;
  slprice1?: number;
  status?: string;
  calltype?: string;
  termType?: string;
  redirectURL?: string;
  returnSoFar?: string;
  potentialReturns?: string;
  prevClose?: number;
}

export interface ITrendingStocksListResponse {
  researchCalls?: IResearchCallItem[];
  stockList?: unknown[];
  totalCount?: number;
  showMore?: boolean;
  data?: {
    researchCalls?: IResearchCallItem[];
    stockList?: unknown[];
    totalCount?: number;
    showMore?: boolean;
  };
}

export const getTrendingStocksListData = async (
  _body: ITrendingStocksListRequest,
): Promise<ITrendingStocksListResponse> => {
  const isClient =
    (globalThis as unknown as { window?: unknown }).window !== undefined;
  const resolvedType =
    _body?.type ??
    ((_body?.tab ?? "").toLowerCase() === "fo" ? "F&O" : "STK");
  const requestBody = {
    entity: _body?.entity,
    type: resolvedType,
    page: _body?.page ?? 1,
    limit: _body?.limit ?? 10,
  };
  const response = (await fetchData({
    options: {
      method: "POST",
      ...(isClient ? {} : { skipCsrf: true }),
    },
    endpoint: withServerTokenSrc(TradingMallApiEndpoints.TRENDING_RESEARCH_CALLS),
    body: requestBody,
    cache: "no-store",
  })) as ITrendingStocksListResponse;

  if (response?.data) {
    LogUtils.debug("getTrendingStocksListData: ", response.data);
    return response;
  }
  return response?.data ?? {};
};

/**
 * Fetches fundamental analysis data for a stock
 * @async
 * @description Retrieves detailed fundamental analysis including financial ratios,
 * balance sheet highlights, and other key metrics. Data is cached for 30 seconds.
 * @param {Object} params - The stock identification parameters
 * @param {string} params.exchId - Exchange identifier (e.g., "NSE", "BSE")
 * @param {string} params.formattedSymbol - Stock symbol in exchange-specific format
 * @returns {Promise<IStockFundamentalsData>} Fundamental analysis data
 * @throws {Error} When the API request fails or returns invalid data
 * @example
 * const fundamentals = await getStockFundamentalsData({ exchId: "NSE", formattedSymbol: "TATASTEEL" });
 */
export const getStockFundamentalsData = async ({
  exchId,
  formattedSymbol,
}: IStockApiRequest): Promise<IStockFundamentalsData> => {
  const response: IStockApiResponse<IStockFundamentalsData> = await fetchData({
    endpoint: TradingMallApiEndpoints.STOCK_FUNDAMENTALS,
    params: { exchId, formattedSymbol },
    revalidate: 45,
  });
  LogUtils.debug("getStockFundamentals: ", response.data);
  return response.data;
};

/**
 * Retrieves comprehensive stock information
 * @async
 * @description Fetches detailed stock information including company info, market data,
 * and other relevant metrics. Returns real-time data without caching.
 * @param {Object} params - The stock identification parameters
 * @param {string} params.exchId - Exchange identifier (e.g., "NSE", "BSE")
 * @param {string} params.formattedSymbol - Stock symbol in exchange-specific format
 * @returns {Promise<IStockInfoData>} Comprehensive stock information
 * @throws {Error} When the API request fails or returns invalid data
 * @example
 * const info = await getStockInfoData({ exchId: "NSE", formattedSymbol: "TATASTEEL" });
 */
export const getStockInfoData = async ({
  exchId,
  formattedSymbol,
}: IStockApiRequest): Promise<IStockInfoData> => {
  const response: IStockApiResponse<IStockInfoData> = await fetchData({
    endpoint: TradingMallApiEndpoints.STOCK_OVERVIEW_INFO,
    params: { exchId, formattedSymbol },
    revalidate: 45,
  });
  LogUtils.debug("getStockOverviewInfo: ", response?.data);
  return response?.data;
};

/**
 * Fetches Top Gainers, Top Losers, and All Stocks
 * @param body
 * @returns
 */
export const getStockChartData = async (
  _body: IStockChartDataRequest,
): Promise<IStockChartData> => {
  const response: IStockApiResponse<IStockChartData> = await fetchData({
    options: {
      method: "POST",
    },
    endpoint: TradingMallApiEndpoints.STOCK_CHART_DATA,
    body: _body,
    revalidate: 45,
  });
  if (response?.data) {
    LogUtils.debug("getStockChartData: ", response.data);
    return response?.data;
  }
  return response?.data;
};

/**
 * Retrieves similar stock data based on the provided request parameters
 *
 * @param _body - Request object containing parameters for fetching similar stocks
 * @returns Promise that resolves to similar stocks data
 *
 * @remarks
 * Currently returns mock data. Implementation for actual API call is commented out.
 * When uncommented, makes a POST request to fetch similar stocks with 30 second revalidation.
 */
export const getSimilarStockData = async (
  exchId: string,
  formattedSymbol: string,
): Promise<ISimilarStocksData> => {
  const _body = {
    exchId: Number(exchId),
    formattedSymbol: formattedSymbol,
    limit: 7,
    offset: 1,
  };

  const response: IStockApiResponse<ISimilarStocksData> = await fetchData({
    options: {
      method: "POST",
      skipCsrf: true,
    },
    endpoint: TradingMallApiEndpoints.SIMILAR_STOCKS,
    body: _body,
    revalidate: 45,
  });

  LogUtils.debug("getSimilarStockData: ", response?.data);
  return response?.data ?? { similarStocks: [] };
};
/**
 * Retrieves financial data for a specified stock.
 *
 * @param {object} params - The request parameters
 * @param {string} params.exchId - The exchange ID
 * @param {string} params.formattedSymbol - The formatted symbol of the stock
 *
 * @returns {Promise<IStockFinancialsData>} A promise that resolves to the stock's financial data
 *
 * @throws {Error} If the API request fails
 */
export const getStockFinancialsData = async ({
  exchId,
  formattedSymbol,
}: IStockApiRequest): Promise<IStockFinancialsData> => {
  const response: IStockApiResponse<IStockFinancialsData> = await fetchData({
    endpoint: TradingMallApiEndpoints.STOCK_FINANCIALS,
    params: { exchId, formattedSymbol },
    revalidate: 45,
  });
  LogUtils.debug("getStockFinancialsData: ", response?.data);
  return response?.data;
};

/**
 * getStockTechnicalSummary
 * @param param0
 * @returns
 */
export const getStockTechnicalSummary = async ({
  exchId,
  formattedSymbol,
}: IStockApiRequest): Promise<IStockTechnicalSummaryData> => {
  const response: IStockApiResponse<IStockTechnicalSummaryData> =
    await fetchData({
      endpoint: TradingMallApiEndpoints.STOCK_TECHNICAL_SUMMARY,
      params: { exchId, formattedSymbol },
      revalidate: 45,
    });
  LogUtils.debug("getStockTechnicalSummary: ", response?.data);
  return response?.data;
};

/**
 * Fetches the shareholding pattern for a specific stock
 * @async
 * @description Retrieves the shareholding pattern data including institutional and retail holdings.
 * This data is not cached and represents the latest available information.
 * @param {Object} params - The stock identification parameters
 * @param {string} params.exchId - Exchange identifier (e.g., "NSE", "BSE")
 * @param {string} params.formattedSymbol - Stock symbol in exchange-specific format
 * @returns {Promise<any>} Shareholding pattern data for the stock
 * @throws {Error} When the API request fails or returns invalid data
 * @example
 * const holding = await getStockShareHolding({ exchId: "NSE", formattedSymbol: "TATASTEEL" });
 */
export const getStockShareHolding = async ({
  exchId,
  formattedSymbol,
}: IStockApiRequest) => {
  const response = await fetchData({
    endpoint: TradingMallApiEndpoints?.STOCK_HOLDING_PATTERN,
    params: { exchId, formattedSymbol },
    revalidate: 45,
  });
  LogUtils.debug("getStockShareHolding: ", response?.data);
  return response?.data;
};

type Listing = {
  shareName: string;
  shareSymbol: string;
  nseToken: number;
  bseToken: number;
};

interface Cache {
  dataMap: Map<string, Listing & { originalSymbol: string }>;
  lastFetched: number; // timestamp in ms
}

let cache: Cache | null = null;

/**
 * fetchInitialListing
 * @param symbol
 * @returns
 */
export const fetchInitialListing = async () => {
  try {
    const response = await fetchData({
      endpoint: TradingMallApiEndpoints.TRADING_LISTING_DATA,
      cache: "no-store",
    });
    const dataMap: Map<string, Listing & { originalSymbol: string }> = new Map(
      response?.data?.listings?.map((i: Listing) => {
        const originalSymbol = i.shareSymbol;
        const modifiedShareSymbol = i.shareSymbol
          ?.replace("&", "-")
          ?.toLowerCase();
        return [modifiedShareSymbol, { ...i, originalSymbol }];
      }),
    );
    LogUtils.debug("fetchInitialListing: ", response?.data);
    return { dataMap, lastFetched: Date.now() };
  } catch (error) {
    console.error("Error fetching initial listing data:", error);
    throw error;
  }
};

/**
 * Gets the timestamp for today's 9:45 AM IST in milliseconds.
 * @returns number - Timestamp for today's 9:45 AM IST in milliseconds
 */
function getTodayRefreshTimestampIST(): number {
  const now = new Date();

  // Current date in UTC
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();

  // 9:45 IST = 9:45 - 5h30m = 4:15 UTC
  const refreshUTC = Date.UTC(year, month, day, 4, 15, 0); // 4:15 UTC is 9:45 IST
  return refreshUTC;
}

/**
 * Retrieves the listing cache for stocks.
 * @returns Promise that resolves to a map of stock listings
 */
export async function getListingCache(): Promise<
  Map<string, Listing & { originalSymbol: string }>
> {
  const refreshTime = getTodayRefreshTimestampIST();
  // Lazy reload if cache is missing or last fetch was before today's 9:45
  if (!cache || cache.lastFetched < refreshTime) {
    console.log(
      "Refreshing listing cache at: ",
      new Date(refreshTime).toLocaleString(),
    );
    cache = await fetchInitialListing();
  }

  if (!cache) {
    throw new Error("Failed to fetch listing cache.");
  }
  return cache?.dataMap;
}

export const getIpoListData = async (
  payload: IIpoListAPIPayload,
): Promise<IIpoListAPIResponse> => {
  const response: IIpoListAPIResponse = await fetchData({
    options: { method: "POST", skipCsrf: true },
    endpoint: TradingMallApiEndpoints.IPO_LIST,
    body: payload,
    revalidate: 45,
  });

  return response;
};

export const getIPOListData = async (
  _body: IIpoListRequest,
): Promise<IIpoListResponse> => {
  const response = await fetchData({
    options: {
      method: "POST",
      skipCsrf: true,
    },
    endpoint: TradingMallApiEndpoints.IPO_LIST,
    body: _body,
    revalidate: 45,
  });
  if (response?.data) {
    LogUtils.debug("getIPOListData: ", response.data);
  }
  return response;
};

export const getCorporateActionData = async (
  exchId: string,
  token: number,
): Promise<ICorporateActions> => {
  const _body = {
    exchId: Number(exchId),
    token,
  };

  const response: IStockApiResponse<ICorporateActions> = await fetchData({
    options: {
      method: "POST",
      skipCsrf: true,
    },
    endpoint: TradingMallApiEndpoints.CORPORATE_ACTION_DATA,
    body: _body,
    revalidate: 45,
  });

  LogUtils.debug("getCorporateActionData: ", response?.data);
  return response?.data;
};

/**
 * Fetches updated share price data for a given exchange and symbol.
 *
 * @param {ISharePriceUpdateParams} params - Contains exchId and symbol.
 * @returns {Promise<ISharePriceUpdateData|null>} Share price update data or null if unavailable.
 */

export const getSharePriceUpdateData = async ({
  exchId,
  symbol,
}: ISharePriceUpdateParams): Promise<ISharePriceUpdateData | null> => {
  try {
    const response = await fetchData({
      endpoint: TradingMallApiEndpoints.SHARE_PRICE_UPDATE,
      body: { exchId, symbol },
      options: {
        method: "POST",
        skipCsrf: true,
      },
      revalidate: 45,
    });

    if (!response?.data) {
      console.warn("Share price update returned no data");
      return null;
    }
    return response.data;
  } catch (error) {
    console.error("Failed to fetch share price update:", error);
    return null;
  }
};

/**
 * Fetches investment returns data for a specific stock
 * @async
 * @description Gets investment returns over multiple periods including 1 month, 3 months
 * @param {Object} params
 * @param {string} params.exchId
 * @param {string} params.formattedSymbol
 * @returns {Promise<IInvestmentReturnsData>}
 * @throws {Error}
 */
export const getInvestmentReturnsData = async ({
  exchId,
  formattedSymbol,
}: IInvestmentReturnsRequest): Promise<IInvestmentReturnsData> => {
  const response: IStockApiResponse<IInvestmentReturnsData> = await fetchData({
    endpoint: TradingMallApiEndpoints.STOCK_TECHNICAL_SUMMARY,
    params: { exchId, formattedSymbol },
    revalidate: 45,
  });
  LogUtils.debug("getInvestmentReturnsData: ", response?.data);
  return response?.data;
};

export const getMarketTodayData = async (
  entity: string,
  limit: number = 25,
  page: number = 1,
): Promise<MarketTodayApiResponse> => {
  const _body = {
    entity,
    page,
    limit,
  };
  const response = await fetchData({
    options: {
      method: "POST",
      skipCsrf: true,
    },
    endpoint: TradingMallApiEndpoints.MARKET_INDICES,
    body: _body,
    revalidate: 45,
  });
  LogUtils.debug("getMarketsTodayData: ", response?.data);
  return response?.data;
};

export interface IndicesPageResult {
  rows: IStockItem[];
  showMore: boolean;
}

function normalizeIndicesRows(payload: any): IStockItem[] {
  const toSafeString = (value: unknown, fallback: string = ""): string => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string") return value;
    if (
      typeof value === "number" ||
      typeof value === "boolean" ||
      typeof value === "bigint"
    ) {
      return `${value}`;
    }
    return fallback;
  };

  const toSafeNumber = (value: unknown, fallback: number = 0): number => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : fallback;
    }
    if (typeof value === "string") {
      const normalized = value.replaceAll(",", "").trim();
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
  };

  const normalizeExchange = (exchange: unknown): number => {
    if (typeof exchange === "number") return exchange;
    if (typeof exchange !== "string") return 0;
    const value = exchange.trim().toUpperCase();
    if (value === "BSE") return 2;
    return 0; // default NSE
  };

  const normalizeRow = (row: any): IStockItem => ({
    // Supports both market-style rows and global-indices rows.
    symbol: toSafeString(row?.symbol ?? row?.exc),
    fullName: toSafeString(
      row?.fullName ?? row?.country ?? row?.symbol ?? row?.exc,
    ),
    ltp: toSafeNumber(row?.ltp ?? row?.cmp),
    changePrice: toSafeString(row?.changePrice ?? row?.chg, "0"),
    perChange: toSafeString(row?.perChange ?? row?.chgPer, "0"),
    exchange: normalizeExchange(row?.exchange),
    token: toSafeNumber(row?.token),
    stockStatus: toSafeString(row?.stockStatus),
    redirectURL: toSafeString(row?.redirectURL),
    logoURL: toSafeString(row?.logoURL),
  });

  if (Array.isArray(payload?.data)) {
    return payload.data.map(normalizeRow);
  }
  if (Array.isArray(payload?.data?.indicesList)) {
    return payload.data.indicesList.map(normalizeRow);
  }
  if (Array.isArray(payload?.data?.stockList)) {
    return payload.data.stockList.map(normalizeRow);
  }
  if (Array.isArray(payload?.indicesList)) {
    return payload.indicesList.map(normalizeRow);
  }
  if (Array.isArray(payload?.stockList)) {
    return payload.stockList.map(normalizeRow);
  }
  return [];
}

export const getGlobalIndicesData = async (
  page: number = 1,
  limit: number = 10,
): Promise<IndicesPageResult> => {
  const response = await fetchData({
    options: {
      method: "POST",
      skipCsrf: true,
    },
    endpoint: TradingMallApiEndpoints.GLOBAL_INDICES,
    body: { page, limit },
    cache: "no-store",
  });

  const rows = normalizeIndicesRows(response);
  return {
    rows,
    showMore:
      typeof response?.data?.showMore === "boolean"
        ? response.data.showMore
        : rows.length >= limit,
  };
};

export const getSectorIndicesData = async (
  page: number = 1,
  limit: number = 10,
): Promise<IndicesPageResult> => {
  const response = await fetchData({
    options: {
      method: "POST",
      skipCsrf: true,
    },
    endpoint: TradingMallApiEndpoints.MARKET_INDICES,
    body: { entity: "sector", page, limit },
    cache: "no-store",
  });

  const rows = normalizeIndicesRows(response);
  return {
    rows,
    showMore:
      typeof response?.data?.showMore === "boolean"
        ? response.data.showMore
        : rows.length >= limit,
  };
};

export const getFilteredIndicesList = async (): Promise<IndexListItem[]> => {
  const response = await fetchData({
    endpoint: TradingMallApiEndpoints.FILTERED_INDICES_LIST,
    options: {
      method: "GET",
      skipCsrf: true,
    },
    revalidate: 45,
  });

  const rawList = response?.data ?? [];

  return (
    rawList as Array<{ symbol: string; token: number; exchange?: number }>
  ).map(({ symbol, token, exchange }) => ({
    symbol,
    token,
    exchange: exchange ?? 0,
  }));
};

export const getNewsFilterData = async (): Promise<INewsFilterData> => {
  const response: INewsFilterApiResponse = await fetchData({
    endpoint: TradingMallApiEndpoints.NEWS_FILTER,
  });
  LogUtils.debug("getNewsFilterData: ", response?.data);

  const raw = response?.data?.filter;

  // Filter out "All" values and normalize filter data
  const safeFilters: INewsFilterData = {
    events: (raw?.events ?? []).filter((value) => value !== "All"),
    index: (raw?.index ?? [])
      .filter((value) => value !== "All")
      .map((value) => (value === "Nifty" ? "Nifty 50" : value)),
    instrument_type: (raw?.instrument_type ?? []).filter(
      (value) => value !== "All",
    ),
    market_cap: (raw?.market_cap ?? []).filter((value) => value !== "All"),
    sector: (raw?.sector ?? []).filter((value) => value !== "All"),
  };

  return safeFilters;
};

/**
 * Fetches news data from the public API
 * Normalizes the Flipit public payload into INewsItem so that
 * all downstream consumers (lists, cards, detail slider) see a consistent shape.
 * @param params - Request parameters for news data
 * @returns Promise that resolves to array of news items
 * @throws Error when API request fails
 */
export const getNewsData = async ({
  newsType,
  filters = [],
  page = 1,
  limit = 20,
}: {
  newsType: "trending" | "other";
  filters?: string[];
  page?: number;
  limit?: number;
}): Promise<INewsItem[]> => {
  try {
    const response: INewsApiResponse = await fetchData({
      endpoint: withServerTokenSrc(TradingMallApiEndpoints.NEWS_PUBLIC),
      cache: "no-store",
      options: getPublicNewsPostOptions(),
      body: {
        newsType,
        filters,
        page,
        limit,
      },
    });

    // If response is undefined or null, the API call failed
    if (!response) {
      LogUtils.error("Failed to fetch news data: API returned no response");
      throw new Error("Failed to fetch news data");
    }

    LogUtils.debug("getNewsData: ", response?.data);

    // Normalize news payload into INewsItem so all consumers see a consistent shape.
    const rawNews = (response?.data?.news ?? []) as unknown as Array<
      INewsItem | INewsCardData
    >;

    const normalized: INewsItem[] = rawNews.map((item) => {
      // Flipit-style card payload from public news API
      if (item && "banner_link" in (item as INewsCardData)) {
        return mapCardDataToNewsItem(item as INewsCardData);
      }
      // Already in INewsItem shape
      return item as INewsItem;
    });

    return normalized;
  } catch (error) {
    LogUtils.error("Failed to fetch news data:", error);
    // Re-throw the error so components can distinguish between API failure and empty data
    throw error;
  }
};

/**
 * Fetches other news data with pagination support from the public API
 * Returns showMore/showLess flags from backend for pagination control
 * @param params - Request parameters for news data
 * @returns Promise that resolves to news data including showMore/showLess flags
 * @throws Error when API request fails
 */
export const getOtherNewsData = async ({
  filters = [],
  page = 1,
  limit = 5,
}: {
  filters?: string[];
  page?: number;
  limit?: number;
}): Promise<IOtherNewsData> => {
  try {
    const response: INewsApiResponse = await fetchData({
      endpoint: withServerTokenSrc(TradingMallApiEndpoints.NEWS_PUBLIC),
      cache: "no-store",
      options: getPublicNewsPostOptions(),
      body: {
        newsType: "other",
        filters,
        page,
        limit,
      },
    });

    // If response is undefined or null, the API call failed
    if (!response) {
      LogUtils.error(
        "Failed to fetch other news data: API returned no response",
      );
      throw new Error("Failed to fetch other news data");
    }

    LogUtils.debug("getOtherNewsData: ", response?.data);

    // Normalize news payload into INewsItem so all consumers see a consistent shape.
    const rawNews = (response?.data?.news ?? []) as unknown as Array<
      INewsItem | INewsCardData
    >;

    const normalized: INewsItem[] = rawNews.map((item) => {
      // Flipit-style card payload from public news API
      if (item && "banner_link" in (item as INewsCardData)) {
        return mapCardDataToNewsItem(item as INewsCardData);
      }
      // Already in INewsItem shape
      return item as INewsItem;
    });

    return {
      news: normalized,
      showMore: response?.data?.showMore ?? false,
      showLess: response?.data?.showLess ?? false,
    };
  } catch (error) {
    LogUtils.error("Failed to fetch other news data:", error);
    // Re-throw the error so components can distinguish between API failure and empty data
    throw error;
  }
};

export const getFlipitNews = async (
  page: number = 1,
  limit: number = 10,
): Promise<FlipitNewsResponse> => {
  try {
    const response = await fetchData({
      endpoint: withServerTokenSrc(TradingMallApiEndpoints.NEWS_PUBLIC),
      options: getPublicNewsPostOptions(),
      body: {
        newsType: "financials",
        page,
        limit,
      },
      revalidate: 45,
    });
    if (!response) {
      LogUtils.error(
        "Failed to fetch financial news: API returned no response",
      );
      throw new Error("Failed to fetch financial news");
    }

    LogUtils.debug("getFlipitNews: ", response?.data);
    return response as FlipitNewsResponse;
  } catch (error) {
    LogUtils.error("Failed to fetch financial news:", error);
    throw error;
  }
};

/**
 * Fetches detailed news data for a specific news item by ID
 * Falls back gracefully if API shape differs (single object vs wrapped)
 * @param id - News item identifier
 */
export const getNewsDetailData = async (
  id: string,
  detailType?: string,
): Promise<INewsItem | null> => {
  try {
    const baseEndpoint = TradingMallApiEndpoints.NEWS_DETAIL.replace("{id}", id);
    // Backend contract: only financials detail API requires extra `type=financials`.
    const resolvedDetailType = detailType?.toString().trim();
    const endpoint =
      resolvedDetailType === "financials"
        ? `${baseEndpoint}&type=financials`
        : baseEndpoint;
    type NewsDetailApiResponse = {
      data?: {
        news?: INewsCardData | INewsCardData[] | null;
      };
    };

    const response = (await fetchData({ endpoint })) as
      | NewsDetailApiResponse
      | null;

    // Backend contract: response shape is { data: { news: <object> } }
    // (Some backends may still wrap the detail in an array, so we normalize.)
    const rawDetail = response?.data?.news;
    const cardData = Array.isArray(rawDetail)
      ? rawDetail[0]
      : rawDetail;

    if (!cardData) {
      return null;
    }

    // Map full Flipit news detail payload → INewsItem so all content is available
    const newsItem = mapCardDataToNewsItem(cardData);
    return newsItem;
  } catch (error) {
    LogUtils.error("Failed to fetch news detail data:", error);
    return null;
  }
};

/**
 * Exchanges OAuth `code` received as a URL query parameter
 * for authenticated session details using encrypted API.
 *
 * @param code - Authorization code from query param
 * @param page - Page number for pagination (default: 1)
 * @param limit - Number of items per page (default: 5)
 * @returns Decrypted response data from backend
 */
export const getHoldings = async (
  code: string,
  page: number = 1,
  limit: number = 5,
): Promise<IResponse<HoldingsResponse> | null> => {
  const { mobileNo } = await getSessionData();

  if (!mobileNo) {
    throw new Error("Missing mobile no");
  }

  if (!code) {
    throw new Error("Missing authorization code");
  }

  try {
    const response = await encryptedRequest<HoldingsResponse>({
      method: "POST",
      url: API_PATHS.GET_HOLDINGS,
      data: {
        mobileNo,
        authCode: code,
        page,
        limit,
      },
    });
    return response;
  } catch (error) {
    console.error("[getHoldings] Failed to fetch holdings:", error);
    return null;
  }
};

/**
 * Saves selected indices data
 * @param _body - Request object containing mobileNo, indices, and accessToken
 * @returns Promise that resolves when indices are saved
 */
export const saveIndicesData = async (_body: {
  mobileNo: string;
  indices: string[];
}): Promise<void> => {
  if (!_body?.mobileNo) {
    throw new Error("Missing Mobile No");
  }

  const response: any = await encryptedRequest({
    method: "POST",
    url: TradingMallApiEndpoints.SAVE_INDICES_DETAILS,
    data: _body,
  });

  return response?.data;
};

/**
 * Gets indices details after applying for indices
 * @param _body - Request object containing mobileNo and accessToken
 * @returns Promise that resolves to MarketTodayApiResponse with updated indices data
 */
export const getIndicesDetails = async (_body: {
  mobileNo: string;
}): Promise<MarketTodayApiResponse> => {
  if (!_body?.mobileNo) {
    throw new Error("Missing Mobile No");
  }

  const response: any = await encryptedRequest({
    method: "POST",
    url: TradingMallApiEndpoints.GET_INDICES_DETAILS,
    data: _body,
  });

  return response?.data;
};

export const getAllStocksData = async ({
  exchId,
  entity,
}: {
  exchId: string;
  entity: string;
}) => {
  try {
    const _body = {
      exchId: Number(exchId),
      entity: entity,
      page: 1,
      limit: 10,
      filters: {
        index: [],
        sector: [],
        mktcap: [],
        alphabetic: [],
      },
    };
    const response = await fetchData({
      options: {
        method: "POST",
      },
      revalidate: 45,
      body: _body,
      endpoint: withServerTokenSrc(TradingMallApiEndpoints.GET_STOCK_LIST),
    });
    return response?.data;
  } catch (error) {
    console.error("Failed to fetch all stocks data:", error);
    return null;
  }
};

/**
 * Fetches watchlist data from the API
 * @async
 * @description Gets watchlist data for a specific watchlist or all watchlists.
 * If wlId is provided, returns specific watchlist; otherwise returns all watchlists.
 * @param {string | number | undefined} wlId - Optional watchlist ID. If empty string or undefined, fetches all watchlists
 * @param {number} page - Page number for pagination (default: 1)
 * @param {number} limit - Number of items per page (default: 20)
 * @returns {Promise<IResponse<any>>} Watchlist data response
 * @throws {Error} When the API request fails or mobileNo is missing
 * @example
 * const allWatchlists = await getWatchList("", 1, 20);
 * const specificWatchlist = await getWatchList("1", 1, 20);
 */
export const getWatchList = async (
  wlId?: string | number,
  page: number = 1,
  limit: number = 20,
): Promise<IResponse<any>> => {
  try {
    const { mobileNo } = await getSessionData();
    const response = await encryptedRequest<any>({
      method: "POST",
      url: API_PATHS.GET_WATCHLIST_DATA,
      data: {
        mobileNo,
        wlId: `${wlId ?? ""}`,
        page: page || 1,
        limit: limit || 20,
      },
    });
    LogUtils.debug("getWatchList: ", response?.data);
    return response;
  } catch (error) {
    console.error("getWatchList error:", error);
    console.error("getWatchList error details:", {
      message: error instanceof Error ? error.message : String(error),
      error,
    });
    throw error;
  }
};

/**
 * Updates watchlist stocks
 * @async
 * @description Updates a watchlist by adding or removing stocks.
 * The wlData parameter should be a pipe-separated string of stocks in format "exchange@token".
 * @param {string} wlData - Pipe-separated string of stocks (e.g., "1@12345|1@67890")
 * @param {string} id - Watchlist ID
 * @param {string} wlName - Watchlist name
 * @returns {Promise<IResponse<any>>} Update response with watchlist data or null
 * @throws {Error} When the API request fails or mobileNo is missing
 * @example
 * const response = await updateWatchList("1@12345|1@67890", "1", "My Watchlist");
 */
export const updateWatchList = async (
  wlData: string,
  id: string,
  wlName: string,
): Promise<IResponse<any>> => {
  try {
    const { mobileNo } = await getSessionData();
    if (!mobileNo) {
      throw new Error("Missing Mobile No");
    }
    const response = await encryptedRequest<any>({
      method: "POST",
      url: API_PATHS.UPDATE_WATCHLIST_DATA,
      data: {
        mobileNo,
        wlName,
        wlId: id,
        wlData,
      },
    });
    LogUtils.debug("updateWatchList: ", response?.data);
    return response;
  } catch (error) {
    console.error("updateWatchList error:", error);
    console.error("updateWatchList error details:", {
      message: error instanceof Error ? error.message : String(error),
      error,
    });
    throw error;
  }
};

export const getPersonalizedNewsData = async (
  props: IPersonalizedNewsRequest,
): Promise<IPersonalizedNewsResponse> => {
  try {
    const { newsType, authCode, filters, page, limit } = props;
    const { mobileNo } = await getSessionData();
    const response = await encryptedRequest<any>({
      method: "POST",
      url: TradingMallApiEndpoints.GET_PERSONALIZED_NEWS_DATA,
      data: {
        mobileNo,
        newsType,
        ...(authCode && { authCode }),
        filters: filters ?? [],
        page: page ?? 1,
        limit: limit ?? 10,
      },
    });
    LogUtils.debug("getPersonalizedNewsData: ", response?.data?.news);
    return response || {};
  } catch (error) {
    console.error("getPersonalizedNewsData error:", error);
    throw error;
  }
};
/**
 * Fetches index scrip information using the trading `indices-info` endpoint.
 * @param token - Instrument token for the index (e.g. 26000 for NIFTY).
 * @param exchId - Exchange identifier (default 0 for indices).
 */
export const getScripInfo = async (
  token: number,
  exchId: number = 0,
): Promise<any> => {
  const body = {
    exchId,
    token,
  };
  const response = await fetchData({
    options: {
      method: "POST",
    },
    endpoint: TradingMallApiEndpoints.INDICES_INFO,
    body,
    revalidate: 45,
  });

  if (response?.data) {
    LogUtils.debug("getScripInfo: ", response.data);
    return response;
  }

  return response?.data;
};

export const getGlobalIndicesBySymbol =
  async (): Promise<IGlobalIndicesResponse> => {
    const response = await fetchData({
      endpoint: TradingMallApiEndpoints.GLOBAL_INDICES_BY_SYMBOL,
      options: { method: "GET", skipCsrf: true },
      revalidate: 45,
    });
    return response as IGlobalIndicesResponse;
  };

export const getFiiAndDiiData = async (): Promise<IFiiAndDiiData> => {
  const response = await fetchData({
    endpoint: TradingMallApiEndpoints.GET_FII_DII_DATA,
    options: { method: "GET", skipCsrf: true },
    revalidate: 45,
  });
  return response as IFiiAndDiiData;
};
